import React, { useEffect, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getCurrentUser } from './store/authSlice';
import { LanguageDetector } from './utils/languageDetection';
import { setLanguage } from './store/languageSlice';
import { AppDispatch, RootState } from './store';
import { useTranslation } from 'react-i18next';
import BaseLayout from './components/BaseLayout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import PartnerDashboard from './pages/PartnerDashboard';
import SubmitLead from './pages/SubmitLead';
import Companies from './pages/Companies';
import Payouts from './pages/Payouts';
import PartnerDeals from './pages/PartnerDeals';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import CompanyManagement from './pages/CompanyManagement';
import DealManagement from './pages/DealManagement';
import PayoutManagement from './pages/PayoutManagement';
import AuditLog from './pages/AuditLog';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import LandingPage from './pages/LandingPage';
import DisabledNotice from './components/DisabledNotice';

const SUPPORTED_LANGUAGES = ['de', 'en'];
const DEFAULT_LANGUAGE = 'de';

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#49ABDB]"></div>
      <p className="text-[#5B5B5B]">Loading...</p>
    </div>
  </div>
);

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user, initialized } = useSelector((state: RootState) => state.auth);
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const languageDetector = new LanguageDetector({
      defaultLanguage: DEFAULT_LANGUAGE,
      supportedLanguages: SUPPORTED_LANGUAGES,
    });

    const detectedLanguage = languageDetector.detectLanguage();
    dispatch(setLanguage(detectedLanguage));
    i18n.changeLanguage(detectedLanguage);
    languageDetector.saveLanguagePreference(detectedLanguage);

    document.documentElement.lang = detectedLanguage;
  }, [dispatch, i18n]);

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  const AuthGuard = ({ children }: { children: React.ReactNode }) => {
    if (!initialized) {
      return <PageLoader />;
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (user?.status === 'BLOCKED') {
      return <DisabledNotice />;
    }
    return <>{children}</>;
  };

  const AdminGuard = ({ children }: { children: React.ReactNode }) => {
    if (!initialized) {
      return <PageLoader />;
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (user?.role !== 'ADMIN' && user?.role !== 'CLOSER') {
      return <Navigate to={`/${currentLanguage}/dashboard`} />;
    }
    return <>{children}</>;
  };

  const AdminOnlyGuard = ({ children }: { children: React.ReactNode }) => {
    if (!initialized) {
      return <PageLoader />;
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (user?.role !== 'ADMIN') {
      return <Navigate to={`/${currentLanguage}/dashboard`} />;
    }
    return <>{children}</>;
  };

  const WrappedRoutes = () => (
    <BaseLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to={`/${currentLanguage}`} replace />} />
          {SUPPORTED_LANGUAGES.map((lang) => (
            <React.Fragment key={lang}>
              <Route path={`/${lang}`} element={<LandingPage />} />
              <Route path={`/${lang}/dashboard`} element={<AuthGuard><PartnerDashboard /></AuthGuard>} />
              <Route path={`/${lang}/submit-lead`} element={<AuthGuard><SubmitLead /></AuthGuard>} />
              <Route path={`/${lang}/companies`} element={<AuthGuard><Companies /></AuthGuard>} />
              <Route path={`/${lang}/payouts`} element={<AuthGuard><Payouts /></AuthGuard>} />
              <Route path={`/${lang}/deals`} element={<AuthGuard><PartnerDeals /></AuthGuard>} />
              <Route path={`/${lang}/settings`} element={<AuthGuard><Settings /></AuthGuard>} />

              <Route path={`/${lang}/admin`} element={<AdminGuard><AdminDashboard /></AdminGuard>} />
              <Route path={`/${lang}/admin/users`} element={<AdminOnlyGuard><UserManagement /></AdminOnlyGuard>} />
              <Route path={`/${lang}/admin/partners`} element={<Navigate to={`/${lang}/admin/users`} replace />} />
              <Route path={`/${lang}/admin/closers`} element={<Navigate to={`/${lang}/admin/users`} replace />} />
              <Route path={`/${lang}/admin/workers`} element={<Navigate to={`/${lang}/admin/users`} replace />} />
              <Route path={`/${lang}/admin/companies`} element={<AdminGuard><CompanyManagement /></AdminGuard>} />
              <Route path={`/${lang}/admin/deals`} element={<AdminGuard><DealManagement /></AdminGuard>} />
              <Route path={`/${lang}/admin/payouts`} element={<AdminOnlyGuard><PayoutManagement /></AdminOnlyGuard>} />
              <Route path={`/${lang}/admin/audit`} element={<AdminOnlyGuard><AuditLog /></AdminOnlyGuard>} />
            </React.Fragment>
          ))}

          <Route path="/dashboard" element={<Navigate to={`/${currentLanguage}/dashboard`} replace />} />
          <Route path="/submit-lead" element={<Navigate to={`/${currentLanguage}/submit-lead`} replace />} />
          <Route path="/companies" element={<Navigate to={`/${currentLanguage}/companies`} replace />} />
          <Route path="/payouts" element={<Navigate to={`/${currentLanguage}/payouts`} replace />} />
          <Route path="/deals" element={<Navigate to={`/${currentLanguage}/deals`} replace />} />
          <Route path="/admin" element={<Navigate to={`/${currentLanguage}/admin`} replace />} />
          <Route path="/admin/companies" element={<Navigate to={`/${currentLanguage}/admin/companies`} replace />} />
          <Route path="/admin/deals" element={<Navigate to={`/${currentLanguage}/admin/deals`} replace />} />
          <Route path="/admin/payouts" element={<Navigate to={`/${currentLanguage}/admin/payouts`} replace />} />
          <Route path="/admin/users" element={<Navigate to={`/${currentLanguage}/admin/users`} replace />} />
          <Route path="/admin/partners" element={<Navigate to={`/${currentLanguage}/admin/users`} replace />} />
          <Route path="/admin/closers" element={<Navigate to={`/${currentLanguage}/admin/users`} replace />} />
          <Route path="/admin/audit" element={<Navigate to={`/${currentLanguage}/admin/audit`} replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BaseLayout>
  );

  const authRoutes = ['/login', '/signup', '/reset-password'];
  const currentPath = location.pathname;
  const needsBaseLayout = !authRoutes.some((route) => currentPath.startsWith(route));

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {needsBaseLayout ? <Route path="/*" element={<WrappedRoutes />} /> : null}
    </Routes>
  );
};

export default AppContent;
